/* eslint react/jsx-no-bind: 0 */
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { injectIntl, defineMessages, FormattedDate } from 'react-intl';
import { decorateIntlMessages } from '../common/rosetta_formatted_message';
import {
  archivesURLPath,
  currentYear,
  currentMonth,
  getFirstOfMonthTimestamp,
  isValidDay,
  isValidArchivesDate,
  publicationDayRange,
  publicationMonthRange,
  publicationYearRange
} from '../../utils/date';
import { CaretDown } from '../common/fa_icons';
import classNames from 'classnames';
import {map, pick, some, every, isNil} from 'lodash';
import { getRelativePath } from '../../utils/url';

const DATE_TYPES = ['year', 'month', 'day'];
const NULL_SELECTION_VALUE = 'none';
const messages = decorateIntlMessages(__filename, defineMessages({
  'year': {
    id: 'year',
    description: 'Translation for the archives drop-down nav year value',
    defaultMessage: 'Year'
  },
  'month': {
    id: 'month',
    description: 'Translation for the archives drop-down nav month value',
    defaultMessage: 'Mon'
  },
  'day': {
    id: 'day',
    description: 'Translation for the archives drop-down nav day value',
    defaultMessage: 'Day'
  },
  'button': {
    id: 'form_button',
    description: 'Translation for the archives drop-down nav "Go" button',
    defaultMessage: 'Go'
  },
  'landing_page_subtitle': {
    id: 'landing_page_subtitle',
    description: 'Translation for the archives subtitle "Take a Look Back"',
    defaultMessage: 'Take a Look Back'
  },
  'archives_subtitle': {
    id: 'archives_subtitle',
    description: 'Translation for the archives subtitle "R29 Archives"',
    defaultMessage: 'R29 Archives'
  },
  'choose_a_date': {
    id: 'choose_a_date',
    description: 'Translation for the archives subtitle "Choose a Date"',
    defaultMessage: 'Choose a Date'
  }
}));

const toOption = (date) =>
  <option key={date} value={date} children={date} />;

const shouldDisablePicker = (dateType, { year, month }) =>
  (dateType === 'day' && some([year, month], isNil))
  || (dateType === 'month' && isNil(year));

const getValueForPicker = (dateState) =>
  dateState === null ? NULL_SELECTION_VALUE : dateState;

export class ArchivesDropDownNavigation extends Component {
  constructor(props, context) {
    super(props, context);

    this.state = {};
    Object.assign(this.state, pick(this.props, DATE_TYPES));
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (every(DATE_TYPES, type => this.props[type] === nextProps[type])) {
      return;
    }
    const nextState = pick(nextProps, DATE_TYPES);
    this.setState(nextState);
  }

  shouldEnableButton = () =>
    (
      every(this.state, isNil)
      || isValidArchivesDate(this.context.edition_data.id, this.state)
    )
    && getRelativePath(archivesURLPath(this.state), this.context.edition_data) !== this.context.location.pathname;

  handleGoButtonClick = (event) => {
    event.preventDefault();
    this.context.router.push({
      pathname: getRelativePath(archivesURLPath(this.state), this.context.edition_data),
      method: 'GET'
    });
  };

  handleSelectChange = (event) => {
    event.preventDefault();
    switch (event.target.name) {
      case 'year': return this.handleYearChange(event);
      case 'month': return this.handleMonthChange(event);
      case 'day': return this.handleDayChange(event);
    }
  };

  handleYearChange = (event) => {
    const { value } = event.currentTarget;
    if (value === NULL_SELECTION_VALUE) {
      this.setState({ year: null, month: null, day: null });
      return;
    }

    const year = Number(value);
    const month =
      year === currentYear && this.state.month > currentMonth
        ? null
        : this.state.month;
    const shouldNullifyDay = !isValidDay({ ...this.state, year, month });
    const day = shouldNullifyDay ? null : this.state.day;

    this.setState({ year, month, day });
  };

  handleMonthChange = (event) => {
    const { value } = event.currentTarget;
    if (value === NULL_SELECTION_VALUE) {
      this.setState({ month: null, day: null });
      return;
    }

    const month = Number(value);
    const shouldNullifyDay = !isValidDay({ ...this.state, month });
    const day = shouldNullifyDay ? null : this.state.day;

    this.setState({ month, day });
  };

  handleDayChange = (event) => {
    const { value } = event.currentTarget;
    if (value === NULL_SELECTION_VALUE) {
      this.setState({ day: null });
      return;
    }

    this.setState({ day: Number(value) });
  };

  years = () =>
    map(
      publicationYearRange(this.context.edition_data.id),
      toOption
    );

  months = () =>
    map(publicationMonthRange(this.state.year), (month) => (
      <FormattedDate month="short"
        key={month}
        value={getFirstOfMonthTimestamp(month)}
        children={formattedMonth =>
          <option value={month} children={formattedMonth} />
        }
      />
    ));

  days = () => map(publicationDayRange(this.state), toOption);

  selectElementForDataType = (dateType) => {
    return (
      <select name={dateType}
        id={`select-${dateType}`}
        disabled={shouldDisablePicker(dateType, this.state)}
        value={getValueForPicker(this.state[dateType])}
        onChange={this.handleSelectChange}
      >
        <option value={NULL_SELECTION_VALUE} key="default">
          {this.props.intl.formatMessage(messages[dateType])}
        </option>
        {dateType === 'year' && this.years()}
        {dateType === 'month' && this.months()}
        {dateType === 'day' && this.days()}
      </select>
    );
  };

  dropDowns = () =>
    map(DATE_TYPES, dateType => (
      <div key={dateType}
        className={classNames('date-picker', {
          'no-date': this.state[dateType] === null
        })}
      >
        {this.selectElementForDataType(dateType)}
        <CaretDown />
      </div>
    ));

  subtitle = () => this.props.year
    ? this.props.intl.formatMessage(messages.archives_subtitle)
    : this.props.intl.formatMessage(messages.landing_page_subtitle);

  render() {
    return (
      <div className="archives-header">
        <div className="archives-header-container">
          <div className="title-wrapper">
            <h4>{this.subtitle()}</h4>
            <h1>{this.props.title}</h1>
          </div>

          <div id="archives-nav">
            <h4>{this.props.intl.formatMessage(messages.choose_a_date)}</h4>

            {this.dropDowns()}
            <button id="archives-submit"
              disabled={!this.shouldEnableButton()}
              onClick={this.handleGoButtonClick}
            >
              {this.props.intl.formatMessage(messages.button)}
            </button>
          </div>
        </div>
        <div className="clearfix" />
      </div>
    );
  }
}

ArchivesDropDownNavigation.propTypes = {
  year: PropTypes.number,
  month: PropTypes.number,
  day: PropTypes.number,
  title: PropTypes.string,
  intl: PropTypes.object.isRequired
};

ArchivesDropDownNavigation.contextTypes = {
  location: PropTypes.object,
  router: PropTypes.shape({
    push: PropTypes.func.isRequired
  }),
  edition_data: PropTypes.object
};

export default injectIntl(ArchivesDropDownNavigation);
